import { useState } from "react";
import { Sparkles, Crown, Truck, Smartphone, Gift, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const emptyForm = { name: "", phone: "", quartier: "", email: "" };

export const DonnaOnboarding = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(emptyForm);
  const [activatePass, setActivatePass] = useState(false);
  const [busy, setBusy] = useState(false);

  const reset = () => { setStep(1); setForm(emptyForm); setActivatePass(false); };

  const openFlow = () => { reset(); setOpen(true); };

  const goProfile = () => setStep(2);
  const submitProfile = () => {
    if (!form.name.trim() || !form.phone.trim()) { toast.error("Nom et téléphone requis"); return; }
    setStep(3);
  };
  const choosePass = (val) => { setActivatePass(val); setStep(4); };

  const finish = async (paymentMethod) => {
    setBusy(true);
    try {
      const r = await api.post("/onboarding/donna", {
        name: form.name.trim(),
        phone: form.phone.trim(),
        quartier: form.quartier.trim(),
        email: form.email.trim(),
        activate_pass: activatePass,
        payment_method: paymentMethod,
      });
      const c = r.data.client;
      toast.success(`${c.name} (${c.member_no}) accueilli(e) — ${r.data.welcome_points} NaturaPoints offerts 🎁`);
      if (activatePass) toast.success("Pass Or activé — -15% sur toutes ses commandes 👑");
      setOpen(false);
      reset();
      onDone && onDone();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Échec de l'accueil");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="donna-open-button"
        onClick={openFlow}
        className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm transition-colors duration-200"
      >
        <Sparkles className="h-4 w-4" /> Accueil Donna
      </button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="max-w-md" data-testid="donna-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-emerald-600" /> Accueil Donna — étape {step}/4
            </DialogTitle>
          </DialogHeader>

          {step === 1 && (
            <div className="space-y-4 py-2" data-testid="donna-step-1">
              <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4">
                <p className="text-sm leading-relaxed text-emerald-900">
                  Bienvenue dans l'univers Vert Distribution ! 🌿 Je suis Donna, votre assistante dédiée.
                  Pour fêter votre arrivée, je vous offre vos <strong>50 premiers NaturaPoints</strong> ! 🎁
                </p>
              </div>
              <button data-testid="donna-start" onClick={goProfile} className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm transition-colors duration-200">
                Commencer <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3 py-2" data-testid="donna-step-2">
              <p className="text-sm text-slate-500">Créons votre profil.</p>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Nom / Prénom</label>
                <input data-testid="donna-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Téléphone (WhatsApp)</label>
                <input data-testid="donna-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+225 07 00 00 00 00" className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Quartier</label>
                <input data-testid="donna-quartier" value={form.quartier} onChange={(e) => setForm({ ...form, quartier: e.target.value })} placeholder="Ex : Cocody, Yopougon…" className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Email (optionnel)</label>
                <input data-testid="donna-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              <button data-testid="donna-continue" onClick={submitProfile} className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm transition-colors duration-200">
                Continuer <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4 py-2" data-testid="donna-step-3">
              <div className="rounded-2xl bg-amber-50 border border-amber-100 p-4">
                <div className="flex items-center gap-2 mb-1.5">
                  <Crown className="h-5 w-5 text-amber-600" />
                  <span className="font-display font-bold text-amber-800">Pass Or</span>
                </div>
                <p className="text-sm leading-relaxed text-amber-900">
                  Activez le <strong>Pass Or</strong> à seulement <strong>2 000 FCFA / 3 mois</strong> et débloquez
                  immédiatement <strong>-15% de réduction</strong> sur toutes vos commandes ! 👑
                </p>
              </div>
              <button data-testid="donna-activate-pass" onClick={() => choosePass(true)} className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm transition-colors duration-200">
                <Crown className="h-4 w-4" /> Activer le Pass Or (2 000 FCFA)
              </button>
              <button data-testid="donna-skip-pass" onClick={() => choosePass(false)} className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 font-semibold text-sm transition-colors duration-200">
                Plus tard
              </button>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-3 py-2" data-testid="donna-step-4">
              <p className="text-sm text-slate-500 flex items-center gap-1.5"><Gift className="h-4 w-4 text-emerald-600" /> Comment souhaitez-vous régler vos commandes ?</p>
              <button data-testid="donna-pay-livraison" disabled={busy} onClick={() => finish("livraison")} className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 border-slate-200 hover:border-emerald-400 hover:bg-emerald-50 text-slate-800 font-semibold text-sm transition-colors duration-200 disabled:opacity-60">
                <Truck className="h-5 w-5 text-emerald-600" /> Paiement à la livraison
              </button>
              <button data-testid="donna-pay-mobile" disabled={busy} onClick={() => finish("mobile_money")} className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 border-slate-200 hover:border-indigo-400 hover:bg-indigo-50 text-slate-800 font-semibold text-sm transition-colors duration-200 disabled:opacity-60">
                <Smartphone className="h-5 w-5 text-indigo-600" /> Mobile Money (Wave ou Orange Money)
              </button>
              {busy && <p className="text-xs text-slate-400 text-center">Création du profil…</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
